import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle, Building, MessageSquare } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Our Address",
      details: [
        "Basement, Bhukand No.-7, Transport Nagar",
        "Bulandshahr, Uttar Pradesh",
        "India - 203001"
      ]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone",
      details: ["+91 9758308200"]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      details: ["shrivishnuudhyog@gmail.com"]
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-800 text-white py-24" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 mb-6">
            <MessageSquare className="w-10 h-10" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Have questions about our products or want to become a distributor? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div 
                key={index}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-all"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-brand-orange mb-4">
                  {info.icon}
                </div>
                <h3 className="font-semibold text-lg mb-3">{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Company Info */}
      <section className="py-20 bg-brand-cream" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="contact-form">
              <h2 className="text-2xl font-semibold mb-2">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you soon.</p>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Message Sent!</h3>
                  <p className="text-gray-600">Thank you for contacting us. We'll respond shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="John Doe"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="+91 98765 43210"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        data-testid="input-subject"
                      >
                        <option value="">Select a subject</option>
                        <option value="product">Product Inquiry</option>
                        <option value="distribution">Distribution Partnership</option>
                        <option value="bulk">Bulk Order</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us how we can help..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="submit-btn"
                  >
                    <Send className="w-5 h-5" />
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Company Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="company-info">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                    <Building className="w-7 h-7 text-brand-orange" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Company Information</h3>
                    <p className="text-gray-500 text-sm">Official registration details</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="py-3 border-b border-gray-100">
                    <p className="text-sm text-gray-500">Company Name</p>
                    <p className="font-semibold">Shri Vishnu Udhyog</p>
                  </div>
                  <div className="py-3 border-b border-gray-100">
                    <p className="text-sm text-gray-500">GSTIN</p>
                    <p className="font-mono text-brand-orange font-semibold">09EALPG7629N1ZW</p>
                  </div>
                  <div className="py-3">
                    <p className="text-sm text-gray-500">Registered Office</p>
                    <p className="font-semibold">Bulandshahr, Uttar Pradesh</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 text-white rounded-2xl p-8" data-testid="inquiry-box">
                <h3 className="text-xl font-semibold mb-4">Become a Distributor</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Interested in distributing Punjabi Chaska products in your area? 
                  We're always looking for reliable partners to expand our reach.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Competitive margins</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Marketing support</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Territory exclusivity options</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-400">
                  Contact us at <span className="text-orange-400">shrivishnuudhyog@gmail.com</span> for distribution inquiries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="section-title">Find Us</h2>
            <p className="text-gray-600">Visit our office in Bulandshahr, Uttar Pradesh</p>
          </div>
          <div className="bg-gray-200 rounded-xl overflow-hidden h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d112134.94715420474!2d77.78825454062049!3d28.40461556556481!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390c9d8e41d939b3%3A0xc6ef97f3fe3eaeb!2sBulandshahr%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1703000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Punjabi Chaska Location"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
